'use client'

import { XIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { ExchangeIcon } from '@/components/widget/exchange-icon'
import {
  buildFilterCandidates,
  type FilterCandidate,
  findActiveAtToken,
  type MarketType,
  matchCandidates,
  removeAtToken,
} from '@/features/dashboard/components/watchlist-wizard/at-filter-match'
import { useCanAutoFocusSearch } from '@/hooks/use-screen'
import { getExchangeIconKey } from '@/lib/configs/exchange-registry'
import { cn } from '@/lib/utils'
import { AssetClass, Exchange } from '@/types/generated/common/types'

export interface WatchlistWizardSearchBarHandle {
  dismissGhost: () => boolean
  isInputFocused: () => boolean
}

interface WatchlistWizardSearchBarProps {
  query: string
  onQueryChange: (value: string) => void
  placeholder: string
  ariaLabel: string
  exchangeFilters: Set<Exchange>
  typeFilters: Set<MarketType>
  assetClassFilters: Set<AssetClass>
  onToggleExchange: (exchange: Exchange) => void
  onToggleType: (type: MarketType) => void
  onToggleAssetClass: (assetClass: AssetClass) => void
  exchangeOptions: { exchange: Exchange; label: string }[]
  typeOptions: { key: MarketType; label: string }[]
  assetClassOptions: { assetClass: AssetClass; label: string }[]
  onActiveTokenChange: (active: boolean) => void
  /** A base-asset filter (e.g. "BTC" — only markets whose base is BTC), shown as a removable pill. */
  baseFilter?: string | null
  onClearBase?: () => void
}

const PILL_STYLES: Record<FilterCandidate['dimension'], string> = {
  exchange: 'border-cat-blue/30 bg-cat-blue/10 text-cat-blue',
  type: 'border-foreground/30 bg-foreground/15 text-foreground',
  assetClass: 'border-cat-violet/30 bg-cat-violet/10 text-cat-violet',
}

export const WatchlistWizardSearchBar = forwardRef<
  WatchlistWizardSearchBarHandle,
  WatchlistWizardSearchBarProps
>(function WatchlistWizardSearchBar(
  {
    query,
    onQueryChange,
    placeholder,
    ariaLabel,
    exchangeFilters,
    typeFilters,
    assetClassFilters,
    onToggleExchange,
    onToggleType,
    onToggleAssetClass,
    exchangeOptions,
    typeOptions,
    assetClassOptions,
    onActiveTokenChange,
    baseFilter,
    onClearBase,
  },
  ref
) {
  const canAutoFocusSearch = useCanAutoFocusSearch()
  const t = useTranslations('watchlistWizard')
  const inputRef = useRef<HTMLInputElement>(null)
  const [caretIndex, setCaretIndex] = useState(0)
  const [ghostIndex, setGhostIndex] = useState(0)
  const [ghostDismissed, setGhostDismissed] = useState(false)

  const candidates = useMemo(
    () => buildFilterCandidates(exchangeOptions, typeOptions, assetClassOptions),
    [exchangeOptions, typeOptions, assetClassOptions]
  )

  const committedPills = useMemo(
    () =>
      candidates.filter((candidate) => {
        if (candidate.dimension === 'exchange') return exchangeFilters.has(candidate.value)
        if (candidate.dimension === 'type') return typeFilters.has(candidate.value)
        return assetClassFilters.has(candidate.value)
      }),
    [candidates, exchangeFilters, typeFilters, assetClassFilters]
  )

  const activeToken = useMemo(() => findActiveAtToken(query, caretIndex), [query, caretIndex])
  const isActive = activeToken !== null

  useEffect(() => {
    onActiveTokenChange(isActive)
  }, [isActive, onActiveTokenChange])

  // Dismissing the ghost (Escape) should stick only until the token you're
  // typing actually changes underneath it — keyed off primitives, not the
  // `activeToken` object reference, which is new on every render.
  useEffect(() => {
    setGhostDismissed(false)
    setGhostIndex(0)
  }, [activeToken?.start, activeToken?.prefix])

  const matches = useMemo(
    () => (activeToken ? matchCandidates(candidates, activeToken.prefix) : []),
    [candidates, activeToken]
  )

  const ghost =
    !ghostDismissed && matches.length > 0 ? matches[ghostIndex % matches.length] : undefined

  useImperativeHandle(
    ref,
    () => ({
      dismissGhost: () => {
        if (!ghost) return false
        setGhostDismissed(true)
        return true
      },
      isInputFocused: () => document.activeElement === inputRef.current,
    }),
    [ghost]
  )

  const toggleCandidate = (candidate: FilterCandidate) => {
    if (candidate.dimension === 'exchange') onToggleExchange(candidate.value)
    else if (candidate.dimension === 'type') onToggleType(candidate.value)
    else onToggleAssetClass(candidate.value)
  }

  const commitGhost = () => {
    if (!activeToken || !ghost) return
    toggleCandidate(ghost)
    const nextCaret = activeToken.start
    onQueryChange(removeAtToken(query, activeToken.start, caretIndex))
    setCaretIndex(nextCaret)
    requestAnimationFrame(() => inputRef.current?.setSelectionRange(nextCaret, nextCaret))
  }

  const removeLastPill = () => {
    const last = committedPills[committedPills.length - 1]
    if (last) toggleCandidate(last)
    else if (baseFilter) onClearBase?.()
  }

  const syncCaret = (event: { currentTarget: HTMLInputElement }) =>
    setCaretIndex(event.currentTarget.selectionStart ?? 0)

  const exchangeIcon = (candidate: Extract<FilterCandidate, { dimension: 'exchange' }>) => {
    const iconKey = getExchangeIconKey(candidate.value)
    return iconKey ? (
      <ExchangeIcon exchange={iconKey} className="size-3.5 shrink-0 rounded-full" />
    ) : null
  }

  return (
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
      {baseFilter ? (
        <span
          className={cn(
            'flex h-6 items-center gap-1 rounded-3xl border px-1 pl-2 text-xs font-medium',
            PILL_STYLES.type
          )}
        >
          {t('basePill', { base: baseFilter })}
          <Button
            variant="ghost"
            size="icon"
            type="button"
            aria-label={t('removeFilterPill', { label: t('basePill', { base: baseFilter }) })}
            onClick={onClearBase}
            className="ml-0.5 p-0.5 hover:opacity-70"
          >
            <XIcon className="size-3" />
          </Button>
        </span>
      ) : null}
      {committedPills.map((pill) => (
        <span
          key={`${pill.dimension}:${pill.value}`}
          className={cn(
            'flex h-6 items-center gap-1 rounded-3xl border px-1 text-xs font-medium',
            pill.dimension !== 'exchange' && 'pl-2',
            PILL_STYLES[pill.dimension]
          )}
        >
          {pill.dimension === 'exchange' ? exchangeIcon(pill) : null}
          {pill.label}
          <Button
            variant="ghost"
            size="icon"
            type="button"
            aria-label={t('removeFilterPill', { label: pill.label })}
            onClick={() => toggleCandidate(pill)}
            className="ml-0.5 p-0.5 hover:opacity-70"
          >
            <XIcon className="size-3" />
          </Button>
        </span>
      ))}
      <div className="relative h-8 min-w-24 flex-1">
        {ghost ? (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 flex items-center overflow-hidden border border-transparent px-2 py-1 text-base whitespace-pre"
          >
            <span className="invisible">{query}</span>
            <span className="text-foreground/35">
              {ghost.label.slice(activeToken?.prefix.length ?? 0)}
            </span>
          </div>
        ) : null}
        <input
          ref={inputRef}
          value={query}
          onChange={(event) => {
            onQueryChange(event.target.value)
            setCaretIndex(event.target.selectionStart ?? event.target.value.length)
          }}
          onClick={syncCaret}
          onKeyUp={syncCaret}
          onSelect={syncCaret}
          onKeyDown={(event) => {
            if (activeToken) {
              if (event.key === 'Tab' || event.key === 'Enter') {
                if (!ghost) return
                event.preventDefault()
                commitGhost()
                return
              }
              if (event.key === 'ArrowDown') {
                if (matches.length === 0) return
                event.preventDefault()
                setGhostIndex((current) => (current + 1) % matches.length)
                return
              }
              if (event.key === 'Escape') {
                // Unreachable when used inside WatchlistWizard (its only current
                // caller): the parent's window capture-phase listener intercepts
                // Escape first and calls stopPropagation(), so this branch never
                // fires there. Kept for standalone reuse of this component.
                event.preventDefault()
                event.stopPropagation()
                setGhostDismissed(true)
                return
              }
              return
            }
            if (event.key === 'Backspace' && caretIndex === 0) {
              removeLastPill()
            }
          }}
          placeholder={placeholder}
          aria-label={ariaLabel}
          autoFocus={canAutoFocusSearch}
          className="placeholder:text-foreground/35 absolute inset-0 w-full min-w-0 border border-transparent bg-transparent px-2 py-1 text-base outline-none"
        />
      </div>
    </div>
  )
})
